import React, {
  useCallback, useRef, useEffect, useState, useMemo,
} from "react";
import {
  ReactFlow,
  addEdge,
  reconnectEdge,
  useNodesState,
  useEdgesState,
  Background,
  Controls,
  MiniMap,
  ReactFlowProvider,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useFlowViewModel } from "../viewmodels/FlowViewModel";
import FlowService from "../services/FlowService"; // Import the new service
import AutomationService from "../services/AutomationService";
import { NormalMessageNode } from "./nodes/NormalMessageNode.js";
import { InteractiveMessageNode } from "./nodes/InteractiveMessageNode.js";
import { MediaMessageNode } from "./nodes/MediaMessageNode.js";
import { RowNode } from "./nodes/RowNode.js";
import { QuestionMessageNode } from "./nodes/QuestionMessageNode.js";
import { FlowMessageNode } from "./nodes/FlowMessageNode.js";
import { WebhookMessageNode } from "./nodes/WebhookMessageNode.js";
import { ConditionMessageNode } from "./nodes/ConditionMessageNode.js";
import { VerticalActionNode } from "./nodes/VerticalActionNode.js";
import { collectFlowAnswerVariables } from "../components/flow-variables";
import {
  Button,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from "@mui/material";
import { Download, Upload, Save, FolderOpen } from "@mui/icons-material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SmartButtonOutlinedIcon from "@mui/icons-material/SmartButtonOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PermMediaOutlinedIcon from "@mui/icons-material/PermMediaOutlined";
import DeviceHubOutlinedIcon from "@mui/icons-material/DeviceHubOutlined";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import WebhookOutlinedIcon from "@mui/icons-material/WebhookOutlined";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { CatalogMessageNode } from "./nodes/CatalogMessageNode.js";
import ServicesService from "../services/ServicesService.js";
import MessageTemplateService from "../services/MessageTemplateService.js";
import PaymentConfigService from "../services/PaymentConfigService.js";
import { TemplateMessageNode } from "./nodes/TemplateMessageNode.js";
import { PaymentMessageNode } from "./nodes/PaymentMessageNode.js";
import { DeletableEdge } from "./edges/DeletableEdge.js";
import { FlowConnectionLine } from "./edges/FlowConnectionLine.js";
import {
  FLOW_DEFAULT_EDGE_OPTIONS,
  FLOW_EDGE_STROKE,
  FLOW_EDGE_STROKE_SELECTED,
  getFlowEdgeMarkerEnd,
  getFlowEdgeStyle,
} from "./edges/flowEdgeStyle.js";

// Add custom styles to prevent overflow issues
const flowStyles = `
  /* Connectors render behind node cards; handles stay on top for dragging */
  .react-flow__edges {
    z-index: 0 !important;
    pointer-events: none;
  }
  .react-flow__edge {
    pointer-events: visibleStroke;
  }
  .react-flow__edge-path {
    stroke: ${FLOW_EDGE_STROKE};
    stroke-width: 2;
    fill: none;
  }
  .react-flow__edge.selected .react-flow__edge-path,
  .react-flow__edge.selectable:focus .react-flow__edge-path,
  .react-flow__edge.selectable:focus-visible .react-flow__edge-path {
    stroke: ${FLOW_EDGE_STROKE_SELECTED};
    stroke-width: 2.5;
  }
  .react-flow__connection-path {
    stroke: ${FLOW_EDGE_STROKE_SELECTED};
    stroke-width: 2;
    fill: none;
  }
  .react-flow__node {
    z-index: 1 !important;
    overflow: visible !important;
  }
  .react-flow__node:hover {
    z-index: 2 !important;
  }
  .react-flow__node.react-flow__node-selected {
    z-index: 3 !important;
  }
  .react-flow__handle {
    z-index: 4 !important;
  }
`;

// Inject styles
if (typeof document !== "undefined") {
  const styleElement = document.createElement("style");
  styleElement.textContent = flowStyles;
  document.head.appendChild(styleElement);
}

const FLOW_PALETTE_ITEMS = [
  { type: "normalMessage", label: "Text", Icon: ChatBubbleOutlineIcon, accent: "#0891b2" },
  { type: "interactiveMessage", label: "Interactive", Icon: SmartButtonOutlinedIcon, accent: "#ea580c" },
  { type: "questionMessage", label: "Question", Icon: HelpOutlineIcon, accent: "#2563eb" },
  { type: "mediaMessage", label: "Media", Icon: PermMediaOutlinedIcon, accent: "#7c3aed" },
  { type: "flowMessage", label: "Flow", Icon: DeviceHubOutlinedIcon, accent: "#16a34a" },
  { type: "conditionNode", label: "Router", Icon: AltRouteIcon, accent: "#7c3aed" },
  { type: "verticalActionNode", label: "Action", Icon: EventAvailableOutlinedIcon, accent: "#0f766e" },
  { type: "webhookMessage", label: "Webhook", Icon: WebhookOutlinedIcon, accent: "#0d9488" },
  { type: "paymentMessage", label: "Payment", Icon: PaymentOutlinedIcon, accent: "#9333ea" },
  { type: "catalogMessage", label: "Catalog", Icon: StorefrontOutlinedIcon, accent: "#0284c7", catalogOnly: true },
  { type: "templateMessage", label: "Template", Icon: ArticleOutlinedIcon, accent: "#ca8a04" },
];

function FlowPaletteDraggable({
  dragType,
  label,
  Icon,
  accent,
  onDragStart,
  disabled = false,
  title = "",
}) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <div
      draggable={!disabled}
      title={title}
      onDragStart={(e) => !disabled && onDragStart(e, dragType)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor: disabled ? "not-allowed" : "grab",
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? "none" : "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        padding: "8px 6px",
        minHeight: 72,
        borderRadius: 8,
        border: `1px solid ${hovered && !disabled ? accent : "#e2e8f0"}`,
        background: hovered && !disabled ? `${accent}0c` : "#fff",
        transition: "border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease",
        boxShadow: hovered && !disabled ? `0 1px 4px ${accent}20` : "none",
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${accent}18`,
          color: accent,
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 16 }} />
      </Box>
      <Typography
        component="span"
        sx={{
          fontSize: 11,
          fontWeight: 600,
          color: "#334155",
          lineHeight: 1.2,
          textAlign: "center",
          userSelect: "none",
        }}
      >
        {label}
      </Typography>
    </div>
  );
}

const nodeTypes = {
  normalMessage: NormalMessageNode,
  interactiveMessage: InteractiveMessageNode,
  mediaMessage: MediaMessageNode,
  catalogMessage: CatalogMessageNode,
  templateMessage: TemplateMessageNode,
  rowNode: RowNode,
  questionMessage: QuestionMessageNode,
  flowMessage: FlowMessageNode,
  webhookMessage: WebhookMessageNode,
  conditionNode: ConditionMessageNode,
  verticalActionNode: VerticalActionNode,
  paymentMessage: PaymentMessageNode,
};

const edgeTypes = {
  deletable: DeletableEdge,
};
const PAYMENT_SCOPE_ORDER_DETAILS = "order_details";
const PAYMENT_SCOPE_PAYMENT_LINK = "payment_link";

console.log("Available node types:", Object.keys(nodeTypes));

// Inner component that uses useReactFlow
const FlowCanvasInner = ({
  viewModel,
  reactFlowWrapper,
  nodes,
  setNodes,
  edges,
  setEdges,
  onNodesChange,
  onEdgesChange,
  chatbotId,
  readOnly = false,
  previewTemplateJson = null,
  defaultChatbotIdForClone = null,
  onBack = null,
  onCloned = null,
}) => {
  const { fitView, setViewport, getViewport } = useReactFlow();

  const [childNodes, setChildNodes] = useState(new Map());

  // Import/Export state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  // const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [importData, setImportData] = useState("");
  const [templateData, setTemplateData] = useState(null);
  const [templateId, setTemplateId] = useState(null);
  const [catalogId, setCatalogId] = useState(null);
  const hasCatalogConnected = useMemo(() => {
    if (catalogId == null) return false;
    const s = String(catalogId).trim();
    return s.length > 0 && s !== "null" && s !== "undefined";
  }, [catalogId]);
  const [hasStoreId, setHasStoreId] = useState(false);
  const [hasActivePaymentConfigurations, setHasActivePaymentConfigurations] = useState(false);
  const [hasActiveOrderDetailsPaymentConfigurations, setHasActiveOrderDetailsPaymentConfigurations] = useState(false);
  const [hasActivePaymentLinkConfigurations, setHasActivePaymentLinkConfigurations] = useState(false);
  const [canUseOrderDetailsPayments, setCanUseOrderDetailsPayments] = useState(true);
  const [approvedTemplates, setApprovedTemplates] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  // const fileInputRef = useRef(null);

  async function fetchCatalogId() {
      ServicesService.getCatalog().then((res) => setCatalogId(res));
    }

    async function fetchAllTemplates() {
      MessageTemplateService.getAllTemplates().then((res) => setApprovedTemplates(res));
    }
    async function fetchPaymentDependencies() {
      try {
        const [whatsapp, paymentConfigs] = await Promise.all([
          ServicesService.getWhatsapp(),
          PaymentConfigService.listLocal(),
        ]);
        const onboardingMode = String(whatsapp?.getOnboardingMode?.() || "").toLowerCase();
        const canUseNativeOrderDetails = onboardingMode !== "business_app";
        setCanUseOrderDetailsPayments(canUseNativeOrderDetails);
        setHasStoreId(Boolean(String(whatsapp?.getStoreId?.() || "").trim()));
        const activeConfigs = (paymentConfigs || []).filter(
          (cfg) => String(cfg?.active_status || "").toLowerCase() === "active"
        );
        const activeOrderDetails = activeConfigs.some(
          (cfg) => String(cfg?.usage_scope || PAYMENT_SCOPE_ORDER_DETAILS).toLowerCase() === PAYMENT_SCOPE_ORDER_DETAILS
        );
        const activePaymentLink = activeConfigs.some(
          (cfg) => String(cfg?.usage_scope || PAYMENT_SCOPE_ORDER_DETAILS).toLowerCase() === PAYMENT_SCOPE_PAYMENT_LINK
        );
        setHasActiveOrderDetailsPaymentConfigurations(canUseNativeOrderDetails && activeOrderDetails);
        setHasActivePaymentLinkConfigurations(activePaymentLink);
        setHasActivePaymentConfigurations((canUseNativeOrderDetails && activeOrderDetails) || activePaymentLink);
      } catch (error) {
        setHasStoreId(false);
        setHasActivePaymentConfigurations(false);
        setHasActiveOrderDetailsPaymentConfigurations(false);
        setHasActivePaymentLinkConfigurations(false);
        setCanUseOrderDetailsPayments(false);
      }
    }
  
    useEffect(() => {
      fetchCatalogId();
      fetchAllTemplates();
      fetchPaymentDependencies();
    }, [])
  

  // NEW: Save flow function
  const handleSaveFlow = async () => {
    if (readOnly) return;
    try {
      const nodes = viewModel.getNodes();
      const invalidPaymentNode = nodes.find(
        (node) =>
          node.type === "paymentMessage" &&
          (
            !node.data
            || (
              (node.data.paymentType || "order_payment") === "order_payment"
              && !String(node.data.configurationId || "").trim()
            )
            || (
              (node.data.paymentType || "order_payment") === "payment_link"
              && !String(node.data.configurationId || "").trim()
            )
          )
      );

      if (invalidPaymentNode) {
        setSnackbar({
          open: true,
          message: "Please select the matching active payment configuration for all Payment Message nodes before saving.",
          severity: "error",
        });
        return;
      }

      const invalidOrkaCheckoutNode = nodes.find(
        (node) =>
          node.type === "paymentMessage"
          && (node?.data?.paymentType || "order_payment") === "orka_checkout"
          && !hasStoreId
          && !hasActivePaymentLinkConfigurations
      );

      if (invalidOrkaCheckoutNode) {
        setSnackbar({
          open: true,
          message: "Orka Checkout requires a connected store or standalone payment-link credentials.",
          severity: "error",
        });
        return;
      }

      const invalidOrderDetailsNode = nodes.find(
        (node) =>
          node.type === "paymentMessage"
          && (node?.data?.paymentType || "order_payment") === "order_payment"
          && (!canUseOrderDetailsPayments || !hasActiveOrderDetailsPaymentConfigurations)
      );

      if (invalidOrderDetailsNode) {
        setSnackbar({
          open: true,
          message: "Native Order Payment requires new-number onboarding and an active native order details configuration.",
          severity: "error",
        });
        return;
      }

      const invalidPaymentLinkNode = nodes.find(
        (node) =>
          node.type === "paymentMessage"
          && (node?.data?.paymentType || "order_payment") === "payment_link"
          && !hasActivePaymentLinkConfigurations
      );

      if (invalidPaymentLinkNode) {
        setSnackbar({
          open: true,
          message: "Payment Link requires an active payment-link credential in Settings.",
          severity: "error",
        });
        return;
      }

      const flowData = {
        nodes: nodes.map((node) => {
          let transformedNode = transformLabelsForExport(node);
          transformedNode = transformSectionsForExport(transformedNode);
          return {
            ...transformedNode,
            isUpdated:
              transformedNode.isUpdated !== undefined
                ? transformedNode.isUpdated
                : false,
          };
        }),
        edges: viewModel.getEdges(),
        viewport: getViewport(),
        metadata: {
          version: "1.0",
          exportedAt: new Date().toISOString(),
          nodeCount: nodes.length,
          edgeCount: viewModel.getEdges().length,
        },
      };

      console.log("=== SAVING FLOW... ===", {
        ...flowData,
        nodesWithFiles: flowData.nodes.filter(
          (n) =>
            n.type === "mediaMessage" && n.data.selectedFile instanceof File
        ),
      });

      // Use the service to save the flow. It handles file uploads automatically.
      const response = await FlowService.saveFlow(flowData, chatbotId, templateId);
      console.log("=== FLOW SAVED, RESPONSE FROM BACKEND ===", response);
      const savedFlow = JSON.parse(response)
      viewModel.clearFlow();

      savedFlow.nodes.forEach((node) => {
        // The backend should have replaced the file with a URL.
        // We ensure data is an object.
        node.data = node.data || {};
        viewModel.service.model.addNode(node);
      });

      savedFlow.edges.forEach((edge) => {
        viewModel.service.model.addEdge(edge);
      });

      // Notify the rest of the app that the model has changed.
      viewModel.notifyListeners();

      // Restore the viewport
      if (savedFlow.viewport) {
        setViewport(savedFlow.viewport);
      }

      setSnackbar({
        open: true,
        message: "Flow saved successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to save flow:", error);
      setSnackbar({
        open: true,
        message: "Failed to save flow: " + (error.message || "Unknown error"),
        severity: "error",
      });
    }
  };

  const handleCloneDefaultChatbot = async () => {
    if (!defaultChatbotIdForClone || !onCloned) return;
    try {
      const created = await AutomationService.cloneDefaultChatbot(defaultChatbotIdForClone);
      const newId = created?.id;
      if (newId) onCloned(newId);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.message || "Failed to add chatbot to your account",
        severity: "error",
      });
    }
  };

  // Child node management functions
  const handleDeleteRowNode = useCallback(
    (rowNodeId, sectionIndex, rowIndex) => {
      // Remove the row node from the flow
      onNodesChange([{ id: rowNodeId, type: "remove" }]);

      // Remove edges connected to this row node
      const edgesToRemove = edges.filter(
        (edge) => edge.source === rowNodeId || edge.target === rowNodeId
      );
      if (edgesToRemove.length > 0) {
        onEdgesChange(
          edgesToRemove.map((edge) => ({ id: edge.id, type: "remove" }))
        );
      }

      // Update child nodes map
      setChildNodes((prev) => {
        const updated = new Map(prev);
        for (const [parentId, childNodeList] of updated.entries()) {
          const filtered = childNodeList.filter((n) => n.id !== rowNodeId);
          if (filtered.length === 0) {
            updated.delete(parentId);
          } else {
            updated.set(parentId, filtered);
          }
        }
        return updated;
      });

      // Remove the row from the parent node's data
      const parentNodeId = rowNodeId.split("-row-")[0];
      const parentNode = nodes.find((n) => n.id === parentNodeId);
      if (parentNode && parentNode.data.sections) {
        const updatedSections = [...parentNode.data.sections];
        if (
          updatedSections[sectionIndex] &&
          updatedSections[sectionIndex].rows
        ) {
          updatedSections[sectionIndex].rows.splice(rowIndex, 1);
          // Update the parent node's sections
          viewModel.updateNode(parentNodeId, { sections: updatedSections });
        }
      }
    },
    [edges, nodes, onNodesChange, onEdgesChange, viewModel]
  );

  const handleSectionsChange = useCallback(
    (parentNodeId, sections, parentPosition) => {

      // Find the parent node to get its position if not provided
      const parentNode = nodes.find((n) => n.id === parentNodeId);
      if (!parentNode) {
        console.warn("Parent node not found:", parentNodeId);
        return;
      }

      // Use provided position or fallback to parent node position
      const position = parentPosition || parentNode.position;
      if (
        !position ||
        typeof position.x === "undefined" ||
        typeof position.y === "undefined"
      ) {
        console.warn(
          "Invalid position for parent node:",
          parentNodeId,
          position
        );
        return;
      }

      // Remove existing child nodes for this parent
      const existingChildNodes = childNodes.get(parentNodeId) || [];
      const existingIds = existingChildNodes.map((n) => n.id);

      if (existingIds.length > 0) {
        const nodesToRemove = nodes.filter((n) => existingIds.includes(n.id));
        if (nodesToRemove.length > 0) {
          onNodesChange(
            nodesToRemove.map((n) => ({ id: n.id, type: "remove" }))
          );
        }
      }

      // Generate new child row nodes
      const newChildNodes = [];
      sections.forEach((section, sectionIndex) => {
        if (section.rows && Array.isArray(section.rows)) {
          section.rows.forEach((row, rowIndex) => {
            const rowNodeId = `${parentNodeId}-row-${sectionIndex}-${rowIndex}`;
            const rowNode = {
              id: rowNodeId,
              type: "rowNode",
              position: {
                x: position.x + 200 + rowIndex * 20, // Offset from parent
                y: position.y + 100 + sectionIndex * 60 + rowIndex * 50, // Stack rows vertically
              },
              data: {
                title: row.title || `Row ${rowIndex + 1}`,
                description: row.description || "No description",
                sectionIndex,
                rowIndex,
                parentNodeId,
                onDeleteRow: (rowId) => {
                  handleDeleteRowNode(rowId, sectionIndex, rowIndex);
                },
                edges: edges.filter(
                  (e) => e.source === rowNodeId || e.target === rowNodeId
                ),
              },
              parentNode: parentNodeId,
            };
            newChildNodes.push(rowNode);
          });
        }
      });

      // Update child nodes map
      setChildNodes((prev) => {
        const updated = new Map(prev);
        if (newChildNodes.length === 0) {
          updated.delete(parentNodeId);
        } else {
          updated.set(parentNodeId, newChildNodes);
        }
        return updated;
      });

      // Add new child nodes to the flow
      if (newChildNodes.length > 0) {
        console.log("=== CREATING ROW NODES ===", {
          count: newChildNodes.length,
          nodes: newChildNodes.map((n) => ({
            id: n.id,
            type: n.type,
            position: n.position,
          })),
        });
        onNodesChange(newChildNodes.map((n) => ({ item: n, type: "add" })));
      }
    },
    [childNodes, nodes, edges, onNodesChange, handleDeleteRowNode]
  );

  // Add logging for node position tracking
  const logNodePositions = useCallback(
    (nodes, action = "update") => {
      nodes.forEach((node, index) => {
      });
    },
    [reactFlowWrapper]
  );

  // Sync ViewModel state with React Flow state
  useEffect(() => {
    const flowNodes = viewModel.getNodes();
    const flowEdges = viewModel.getEdges();
    const availableVariables = collectFlowAnswerVariables(flowNodes);
    // Add delete handlers to nodes
    const nodesWithHandlers = flowNodes.map((viewModelNode) => {
      const nodeData = {
        ...viewModelNode.data,
        onDelete: (id) => {
          viewModel.deleteNode(id);
        },
        edges: flowEdges,
        availableVariables,
        workflowNodes: flowNodes,
        hasStoreId,
        hasActivePaymentConfigurations,
        hasActiveOrderDetailsPaymentConfigurations,
        hasActivePaymentLinkConfigurations,
        canUseOrderDetailsPayments,
        hasCatalogConnected,
      };

      // Wire handlers for questionMessage so edits propagate into the model
      if (viewModelNode.type === "questionMessage") {
        nodeData.onChangeBody = (id, value) =>
          viewModel.updateNode(id, { body: value, isUpdated: true });
        nodeData.onChangeLabels = (id, value) =>
          viewModel.updateNode(id, { labels: value, isUpdated: true });
        nodeData.onChangeAnswerVariable = (id, value) =>
          viewModel.updateNode(id, { answerVariable: value, isUpdated: true });
        nodeData.onChangeQuestionValidation = (id, value) =>
          viewModel.updateNode(id, { ...value, isUpdated: true });
      } else if (viewModelNode.type === "flowMessage") {
        nodeData.onChangeBody = (id, value) =>
          viewModel.updateNode(id, { body: value, isUpdated: true });
        nodeData.onChangeButtonLabel = (id, value) =>
          viewModel.updateNode(id, { buttonLabel: value, isUpdated: true });
        nodeData.onChangeFlowId = (id, value) =>
          viewModel.updateNode(id, { flowId: value, isUpdated: true });
        nodeData.onChangeFlowName = (id, value) =>
          viewModel.updateNode(id, { flowName: value, isUpdated: true });
        nodeData.onChangeAnswerVariable = (id, value) =>
          viewModel.updateNode(id, { answerVariable: value, isUpdated: true });
        nodeData.onChangeHandlerType = (id, value) =>
          viewModel.updateNode(id, { handlerType: value, isUpdated: true });
      } else if (viewModelNode.type === "conditionNode") {
        nodeData.onChangeRouter = (id, value) =>
          viewModel.updateNode(id, { ...value, isUpdated: true });
        nodeData.onChangeConditions = (id, value) =>
          viewModel.updateNode(id, { conditions: value, isUpdated: true });
      } else if (viewModelNode.type === "verticalActionNode") {
        nodeData.onChangeVerticalAction = (id, value) =>
          viewModel.updateNode(id, { ...value, isUpdated: true });
      } else if (viewModelNode.type === "webhookMessage") {
        nodeData.onChangeMethod = (id, value) =>
          viewModel.updateNode(id, { method: "POST", isUpdated: true });
        nodeData.onChangeUrl = (id, value) =>
          viewModel.updateNode(id, { url: value, isUpdated: true });
        nodeData.onChangeHeadersEnabled = (id, value) =>
          viewModel.updateNode(id, { headersEnabled: value, isUpdated: true });
        nodeData.onChangeHeaders = (id, value) =>
          viewModel.updateNode(id, { headers: value, isUpdated: true });
        nodeData.onChangeBodyEnabled = (id, value) =>
          viewModel.updateNode(id, { bodyEnabled: value, isUpdated: true });
        nodeData.onChangeRequestBody = (id, value) =>
          viewModel.updateNode(id, { requestBody: value, isUpdated: true });
        nodeData.onChangeTestEnabled = (id, value) =>
          viewModel.updateNode(id, { testEnabled: value, isUpdated: true });
        nodeData.onChangeTestVariables = (id, value) =>
          viewModel.updateNode(id, { testVariables: value, isUpdated: true });
        nodeData.onChangeTestStatusCode = (id, value) =>
          viewModel.updateNode(id, { testStatusCode: value, isUpdated: true });
        nodeData.onChangeTestResponseBody = (id, value) =>
          viewModel.updateNode(id, { testResponseBody: value, isUpdated: true });
        nodeData.onChangeSaveResponseAsVariables = (id, value) =>
          viewModel.updateNode(id, { saveResponseAsVariables: value, isUpdated: true });
        nodeData.onChangeResponseRoutingEnabled = (id, value) =>
          viewModel.updateNode(id, { responseRoutingEnabled: value, isUpdated: true });
        nodeData.onChangeExpectedStatuses = (id, value) =>
          viewModel.updateNode(id, { expectedStatuses: value, isUpdated: true });
      } else if (viewModelNode.type === "paymentMessage") {
        const paymentNodeId = viewModelNode.id;
        nodeData.onChangePaymentType = (_id, value) =>
          viewModel.updateNode(paymentNodeId, { paymentType: value, isUpdated: true });
        nodeData.onChangeHeader = (_id, value) =>
          viewModel.updateNode(paymentNodeId, { header: value, isUpdated: true });
        nodeData.onChangeBody = (_id, value) =>
          viewModel.updateNode(paymentNodeId, { body: value, isUpdated: true });
        nodeData.onChangeFooter = (_id, value) =>
          viewModel.updateNode(paymentNodeId, { footer: value, isUpdated: true });
        nodeData.onChangePaymentItems = (_id, value) =>
          viewModel.updateNode(paymentNodeId, { paymentItems: value, isUpdated: true });
        nodeData.onChangeConfigurationId = (_id, value) =>
          viewModel.updateNode(paymentNodeId, { configurationId: value, isUpdated: true });
        nodeData.onChangeConfigurationName = (_id, value) =>
          viewModel.updateNode(paymentNodeId, { configurationName: value, isUpdated: true });
      }

      return {
        ...viewModelNode,
        data: nodeData,
      };
    });

    setNodes(nodesWithHandlers);
    setEdges(flowEdges);
    logNodePositions(nodesWithHandlers, "sync");
  }, [
    viewModel,
    setNodes,
    setEdges,
    logNodePositions,
    hasStoreId,
    hasActivePaymentConfigurations,
    hasActiveOrderDetailsPaymentConfigurations,
    hasActivePaymentLinkConfigurations,
    canUseOrderDetailsPayments,
    hasCatalogConnected,
  ]);

  // Force re-render when ViewModel state changes
  useEffect(() => {

    // Initial fit view only if nodes exist and we're not already positioned
    if (nodes.length > 0) {
      const viewport = getViewport();
      const isAtDefaultPosition =
        viewport.x === 0 && viewport.y === 0 && viewport.zoom === 1;

      if (isAtDefaultPosition) {
        setTimeout(() => {
          fitView({ padding: 0.1 });
        }, 200);
      }
    }
    const unsubscribe = viewModel.subscribe(() => {
      const flowNodes = viewModel.getNodes();
      const flowEdges = viewModel.getEdges();
      const availableVariables = collectFlowAnswerVariables(flowNodes);

      // Always update edges to ensure nodes have current connection state
      // Only update node positions if node count changes to prevent overwriting during drag
      if (flowNodes.length !== nodes.length) {
        const nodesWithHandlers = flowNodes.map((viewModelNode) => {
          const nodeData = {
            ...viewModelNode.data,
            onDelete: (id) => {
              viewModel.deleteNode(id);
            },
            edges: flowEdges,
            availableVariables,
            workflowNodes: flowNodes,
            hasStoreId,
            hasActivePaymentConfigurations,
            hasActiveOrderDetailsPaymentConfigurations,
            hasActivePaymentLinkConfigurations,
            canUseOrderDetailsPayments,
            hasCatalogConnected,
            // Force re-render by adding a timestamp
            lastEdgeUpdate: Date.now(),
          };

          if (viewModelNode.type === "mediaMessage") {
            // Wire up the onChange handlers for MediaMessageNode
            nodeData.onChangeFile = (id, file) =>
              viewModel.updateNode(id, { selectedFile: file });
            nodeData.onChangeCaption = (id, caption) =>
              viewModel.updateNode(id, { caption });
            nodeData.onChangeMediaType = (id, mediaType) =>
              viewModel.updateNode(id, { mediaType });
            nodeData.onChangeFileName = (id, fileName) =>
              viewModel.updateNode(id, { fileName });
            nodeData.onChangeFilePath = (id, filePath) =>
              viewModel.updateNode(id, { filePath });
            nodeData.onChangePreviewUrl = (id, previewUrl) =>
              viewModel.updateNode(id, { previewUrl });
          } else if (viewModelNode.type === "interactiveMessage") {
            // Wire up the onChange handlers for InteractiveMessageNode
            nodeData.onChangeHeader = (id, value) =>
              viewModel.updateNode(id, { header: value });
            nodeData.onChangeBody = (id, value) =>
              viewModel.updateNode(id, { body: value });
            nodeData.onChangeFooter = (id, value) =>
              viewModel.updateNode(id, { footer: value });
            nodeData.onChangeLabels = (id, value) =>
              viewModel.updateNode(id, { labels: value });
            nodeData.onChangeSections = (id, value) =>
              viewModel.updateNode(id, { sections: value });
            nodeData.onChangeListHeader = (id, value) =>
              viewModel.updateNode(id, { listHeader: value });
            nodeData.onChangeMessageType = (id, value) =>
              viewModel.updateNode(id, { messageType: value });
            nodeData.onChangeAnswerVariable = (id, value) =>
              viewModel.updateNode(id, {
                answerVariable: value,
                isUpdated: true,
              });
          } else if (viewModelNode.type === "normalMessage") {
            // Wire up the onChange handlers for NormalMessageNode
            nodeData.onChangeText = (id, value) =>
              viewModel.updateNode(id, { text: value });
          } else if (viewModelNode.type === "catalogMessage") {
            nodeData.onChangeText = (id, value) =>
              viewModel.updateNode(id, { text: value });
            nodeData.onChangeProduct = (id, value) =>
              viewModel.updateNode(id, { products: value });
          } else if (viewModelNode.type === "templateMessage") {
            nodeData.onChangeTemplate = (id, value) =>
              viewModel.updateNode(id, { templateId: value });
            nodeData.onChangeVariables = (id, value) =>
              viewModel.updateNode(id, { variables: value });
          } else if (viewModelNode.type === "questionMessage") {
            nodeData.onChangeBody = (id, value) =>
              viewModel.updateNode(id, { body: value, isUpdated: true });
            nodeData.onChangeLabels = (id, value) =>
              viewModel.updateNode(id, { labels: value, isUpdated: true });
            nodeData.onChangeAnswerVariable = (id, value) =>
              viewModel.updateNode(id, {
                answerVariable: value,
                isUpdated: true,
              });
            nodeData.onChangeQuestionValidation = (id, value) =>
              viewModel.updateNode(id, { ...value, isUpdated: true });
          } else if (viewModelNode.type === "flowMessage") {
            nodeData.onChangeBody = (id, value) =>
              viewModel.updateNode(id, { body: value, isUpdated: true });
            nodeData.onChangeButtonLabel = (id, value) =>
              viewModel.updateNode(id, { buttonLabel: value, isUpdated: true });
            nodeData.onChangeFlowId = (id, value) =>
              viewModel.updateNode(id, { flowId: value, isUpdated: true });
            nodeData.onChangeFlowName = (id, value) =>
              viewModel.updateNode(id, { flowName: value, isUpdated: true });
            nodeData.onChangeAnswerVariable = (id, value) =>
              viewModel.updateNode(id, {
                answerVariable: value,
                isUpdated: true,
              });
            nodeData.onChangeHandlerType = (id, value) =>
              viewModel.updateNode(id, { handlerType: value, isUpdated: true });
          } else if (viewModelNode.type === "conditionNode") {
            nodeData.onChangeRouter = (id, value) =>
              viewModel.updateNode(id, { ...value, isUpdated: true });
            nodeData.onChangeConditions = (id, value) =>
              viewModel.updateNode(id, { conditions: value, isUpdated: true });
          } else if (viewModelNode.type === "verticalActionNode") {
            nodeData.onChangeVerticalAction = (id, value) =>
              viewModel.updateNode(id, { ...value, isUpdated: true });
          } else if (viewModelNode.type === "webhookMessage") {
            nodeData.onChangeMethod = (id, value) =>
              viewModel.updateNode(id, { method: "POST", isUpdated: true });
            nodeData.onChangeUrl = (id, value) =>
              viewModel.updateNode(id, { url: value, isUpdated: true });
            nodeData.onChangeHeadersEnabled = (id, value) =>
              viewModel.updateNode(id, { headersEnabled: value, isUpdated: true });
            nodeData.onChangeHeaders = (id, value) =>
              viewModel.updateNode(id, { headers: value, isUpdated: true });
            nodeData.onChangeBodyEnabled = (id, value) =>
              viewModel.updateNode(id, { bodyEnabled: value, isUpdated: true });
            nodeData.onChangeRequestBody = (id, value) =>
              viewModel.updateNode(id, { requestBody: value, isUpdated: true });
            nodeData.onChangeTestEnabled = (id, value) =>
              viewModel.updateNode(id, { testEnabled: value, isUpdated: true });
            nodeData.onChangeTestVariables = (id, value) =>
              viewModel.updateNode(id, { testVariables: value, isUpdated: true });
            nodeData.onChangeTestStatusCode = (id, value) =>
              viewModel.updateNode(id, { testStatusCode: value, isUpdated: true });
            nodeData.onChangeTestResponseBody = (id, value) =>
              viewModel.updateNode(id, { testResponseBody: value, isUpdated: true });
            nodeData.onChangeSaveResponseAsVariables = (id, value) =>
              viewModel.updateNode(id, { saveResponseAsVariables: value, isUpdated: true });
            nodeData.onChangeResponseRoutingEnabled = (id, value) =>
              viewModel.updateNode(id, { responseRoutingEnabled: value, isUpdated: true });
            nodeData.onChangeExpectedStatuses = (id, value) =>
              viewModel.updateNode(id, { expectedStatuses: value, isUpdated: true });
          } else if (viewModelNode.type === "paymentMessage") {
            const paymentNodeId = viewModelNode.id;
            nodeData.onChangePaymentType = (_id, value) =>
              viewModel.updateNode(paymentNodeId, { paymentType: value, isUpdated: true });
            nodeData.onChangeHeader = (_id, value) =>
              viewModel.updateNode(paymentNodeId, { header: value, isUpdated: true });
            nodeData.onChangeBody = (_id, value) =>
              viewModel.updateNode(paymentNodeId, { body: value, isUpdated: true });
            nodeData.onChangeFooter = (_id, value) =>
              viewModel.updateNode(paymentNodeId, { footer: value, isUpdated: true });
            nodeData.onChangePaymentItems = (_id, value) =>
              viewModel.updateNode(paymentNodeId, { paymentItems: value, isUpdated: true });
            nodeData.onChangeConfigurationId = (_id, value) =>
              viewModel.updateNode(paymentNodeId, { configurationId: value, isUpdated: true });
            nodeData.onChangeConfigurationName = (_id, value) =>
              viewModel.updateNode(paymentNodeId, { configurationName: value, isUpdated: true });
          }

          return {
            ...viewModelNode,
            data: nodeData,
          };
        });

        setNodes(nodesWithHandlers);
        setEdges(flowEdges);

        // Only fit view if no nodes exist yet (first node)
        if (nodesWithHandlers.length === 1 && nodes.length === 0) {
          setTimeout(() => {
            fitView({ padding: 0.1 });
          }, 100);
        }
      } else {
        // Check if edges actually changed to avoid unnecessary updates
        const currentEdgeIds = edges.map((e) => e.id).sort();
        const newEdgeIds = flowEdges.map((e) => e.id).sort();
        const edgesChanged =
          JSON.stringify(currentEdgeIds) !== JSON.stringify(newEdgeIds);

        // Always update nodes when edges change OR when ViewModel data changes
        if (edgesChanged || flowEdges.length !== edges.length) {

          // Update nodes with new edges data without changing positions
          const nodesWithUpdatedEdges = nodes.map((node) => {
            const viewModelNode = flowNodes.find((vn) => vn.id === node.id);
            if (viewModelNode) {
              // Always use React Flow position, never ViewModel position
              const finalPosition = node.position;

              return {
                ...node,
                // Always preserve current position from React Flow, never use ViewModel position
                position: finalPosition,
                data: {
                  ...node.data,
                  edges: flowEdges,
                  // Force re-render by adding a timestamp
                  lastEdgeUpdate: Date.now(),
                },
              };
            }
            return node;
          });

          setNodes(nodesWithUpdatedEdges);
          setEdges(flowEdges);
        } else {
          // Check if any node data has changed in the ViewModel
          const hasDataChanges = nodes.some((node) => {
            const viewModelNode = flowNodes.find((vn) => vn.id === node.id);
            if (!viewModelNode) return false;

            // Compare node data (excluding handlers and position)
            const {
              position: _,
              onDelete: __,
              edges: ___,
              lastEdgeUpdate: ____,
              ...nodeData
            } = node.data;
            const {
              position: _____,
              onDelete: ______,
              edges: _______,
              lastEdgeUpdate: ________,
              ...viewModelData
            } = viewModelNode.data;

            return JSON.stringify(nodeData) !== JSON.stringify(viewModelData);
          });

          if (hasDataChanges) {
            // Update nodes with new ViewModel data while preserving positions
            const nodesWithUpdatedData = nodes.map((node) => {
              const viewModelNode = flowNodes.find((vn) => vn.id === node.id);
              if (viewModelNode) {
                const nodeData = {
                  ...viewModelNode.data, // Use ViewModel data
                  onDelete: (id) => {
                    viewModel.deleteNode(id);
                  },
                  edges: flowEdges,
                  availableVariables,
                  hasStoreId,
                  hasActivePaymentConfigurations,
                  hasActiveOrderDetailsPaymentConfigurations,
                  hasActivePaymentLinkConfigurations,
                  canUseOrderDetailsPayments,
                  hasCatalogConnected,
                  lastEdgeUpdate: Date.now(),
                };

                // Wire up handlers for different node types
                if (viewModelNode.type === "mediaMessage") {
                  nodeData.onChangeFile = (id, file) =>
                    viewModel.updateNode(id, { selectedFile: file });
                  nodeData.onChangeCaption = (id, caption) =>
                    viewModel.updateNode(id, { caption });
                  nodeData.onChangeMediaType = (id, mediaType) =>
                    viewModel.updateNode(id, { mediaType });
                  nodeData.onChangeFileName = (id, fileName) =>
                    viewModel.updateNode(id, { fileName });
                  nodeData.onChangeFilePath = (id, filePath) =>
                    viewModel.updateNode(id, { filePath });
                  nodeData.onChangePreviewUrl = (id, previewUrl) =>
                    viewModel.updateNode(id, { previewUrl });
                } else if (viewModelNode.type === "interactiveMessage") {
                  nodeData.onChangeHeader = (id, value) =>
                    viewModel.updateNode(id, { header: value });
                  nodeData.onChangeBody = (id, value) =>
                    viewModel.updateNode(id, { body: value });
                  nodeData.onChangeFooter = (id, value) =>
                    viewModel.updateNode(id, { footer: value });
                  nodeData.onChangeLabels = (id, value) =>
                    viewModel.updateNode(id, { labels: value });
                  nodeData.onChangeSections = (id, value) =>
                    viewModel.updateNode(id, { sections: value });
                  nodeData.onChangeListHeader = (id, value) =>
                    viewModel.updateNode(id, { listHeader: value });
                  nodeData.onChangeMessageType = (id, value) =>
                    viewModel.updateNode(id, { messageType: value });
                  nodeData.onChangeAnswerVariable = (id, value) =>
                    viewModel.updateNode(id, {
                      answerVariable: value,
                      isUpdated: true,
                    });
                } else if (viewModelNode.type === "normalMessage") {
                  nodeData.onChangeText = (id, value) =>
                    viewModel.updateNode(id, { text: value });
                } else if (viewModelNode.type === "catalogMessage") {
                  nodeData.onChangeText = (id, value) =>
                    viewModel.updateNode(id, { text: value });
                  nodeData.onChangeProduct = (id, value) =>
                    viewModel.updateNode(id, { products: value });
                } else if (viewModelNode.type === "templateMessage") {
                  nodeData.onChangeTemplate = (id, value) =>
                    viewModel.updateNode(id, { templateId: value });
                  nodeData.onChangeVariables = (id, value) =>
                    viewModel.updateNode(id, { variables: value });
                } else if (viewModelNode.type === "questionMessage") {
                  nodeData.onChangeBody = (id, value) =>
                    viewModel.updateNode(id, { body: value, isUpdated: true });
                  nodeData.onChangeLabels = (id, value) =>
                    viewModel.updateNode(id, {
                      labels: value,
                      isUpdated: true,
                    });
                  nodeData.onChangeAnswerVariable = (id, value) =>
                    viewModel.updateNode(id, {
                      answerVariable: value,
                      isUpdated: true,
                    });
                  nodeData.onChangeQuestionValidation = (id, value) =>
                    viewModel.updateNode(id, { ...value, isUpdated: true });
                } else if (viewModelNode.type === "verticalActionNode") {
                  nodeData.onChangeVerticalAction = (id, value) =>
                    viewModel.updateNode(id, { ...value, isUpdated: true });
                } else if (viewModelNode.type === "webhookMessage") {
                  nodeData.onChangeMethod = (id, value) =>
                    viewModel.updateNode(id, { method: "POST", isUpdated: true });
                  nodeData.onChangeUrl = (id, value) =>
                    viewModel.updateNode(id, { url: value, isUpdated: true });
                  nodeData.onChangeHeadersEnabled = (id, value) =>
                    viewModel.updateNode(id, { headersEnabled: value, isUpdated: true });
                  nodeData.onChangeHeaders = (id, value) =>
                    viewModel.updateNode(id, { headers: value, isUpdated: true });
                  nodeData.onChangeBodyEnabled = (id, value) =>
                    viewModel.updateNode(id, { bodyEnabled: value, isUpdated: true });
                  nodeData.onChangeRequestBody = (id, value) =>
                    viewModel.updateNode(id, { requestBody: value, isUpdated: true });
                  nodeData.onChangeTestEnabled = (id, value) =>
                    viewModel.updateNode(id, { testEnabled: value, isUpdated: true });
                  nodeData.onChangeTestVariables = (id, value) =>
                    viewModel.updateNode(id, { testVariables: value, isUpdated: true });
                  nodeData.onChangeTestStatusCode = (id, value) =>
                    viewModel.updateNode(id, { testStatusCode: value, isUpdated: true });
                  nodeData.onChangeTestResponseBody = (id, value) =>
                    viewModel.updateNode(id, { testResponseBody: value, isUpdated: true });
                  nodeData.onChangeSaveResponseAsVariables = (id, value) =>
                    viewModel.updateNode(id, { saveResponseAsVariables: value, isUpdated: true });
                  nodeData.onChangeResponseRoutingEnabled = (id, value) =>
                    viewModel.updateNode(id, { responseRoutingEnabled: value, isUpdated: true });
                  nodeData.onChangeExpectedStatuses = (id, value) =>
                    viewModel.updateNode(id, { expectedStatuses: value, isUpdated: true });
                } else if (viewModelNode.type === "paymentMessage") {
                  const paymentNodeId = viewModelNode.id;
                  nodeData.onChangePaymentType = (_id, value) =>
                    viewModel.updateNode(paymentNodeId, { paymentType: value, isUpdated: true });
                  nodeData.onChangeHeader = (_id, value) =>
                    viewModel.updateNode(paymentNodeId, { header: value, isUpdated: true });
                  nodeData.onChangeBody = (_id, value) =>
                    viewModel.updateNode(paymentNodeId, { body: value, isUpdated: true });
                  nodeData.onChangeFooter = (_id, value) =>
                    viewModel.updateNode(paymentNodeId, { footer: value, isUpdated: true });
                  nodeData.onChangePaymentItems = (_id, value) =>
                    viewModel.updateNode(paymentNodeId, { paymentItems: value, isUpdated: true });
                  nodeData.onChangeConfigurationId = (_id, value) =>
                    viewModel.updateNode(paymentNodeId, { configurationId: value, isUpdated: true });
                  nodeData.onChangeConfigurationName = (_id, value) =>
                    viewModel.updateNode(paymentNodeId, { configurationName: value, isUpdated: true });
                }

                return {
                  ...node,
                  position: node.position, // Preserve React Flow position
                  data: nodeData,
                };
              }
              return node;
            });

            setNodes(nodesWithUpdatedData);
            setEdges(flowEdges);
          } else {
            console.log("No edge changes detected, skipping node update");
          }
        }
      }
    });

    return unsubscribe;
  }, [
    viewModel,
    setNodes,
    setEdges,
    nodes.length,
    edges,
    nodes,
    fitView,
    hasStoreId,
    hasActivePaymentConfigurations,
    hasActiveOrderDetailsPaymentConfigurations,
    hasActivePaymentLinkConfigurations,
    canUseOrderDetailsPayments,
    hasCatalogConnected,
  ]);

  const onConnect = useCallback(
    (params) => {
      if (readOnly) return;
      try {
        viewModel.addEdge(
          params.source,
          params.target,
          params.sourceHandle,
          params.targetHandle
        );
        setEdges((eds) => addEdge(params, eds));
      } catch (error) {
        console.error("Failed to create connection:", error);
      }
    },
    [readOnly, viewModel, setEdges]
  );

  const onReconnect = useCallback(
    (oldEdge, newConnection) => {
      if (readOnly) return;
      try {
        viewModel.deleteEdge(oldEdge.id);
        viewModel.addEdge(
          newConnection.source,
          newConnection.target,
          newConnection.sourceHandle ?? undefined,
          newConnection.targetHandle ?? undefined
        );
        setEdges((eds) => reconnectEdge(oldEdge, newConnection, eds));
      } catch (error) {
        console.error("Failed to reconnect edge:", error);
      }
    },
    [readOnly, viewModel, setEdges]
  );

  const handleNodesChange = useCallback(
    (changes) => {
      if (readOnly) return;
      console.log("Changes:", changes);

      // Get canvas dimensions for boundary checking
      const canvasBounds = reactFlowWrapper.current?.getBoundingClientRect();
      console.log("Canvas bounds:", canvasBounds);

      // Handle different types of node changes
      changes.forEach((change) => {
        switch (change.type) {
          case "remove":
            viewModel.deleteNode(change.id);
            break;
          case "position":
            if (change.position) {
              // Check if node is near boundaries and constrain if necessary
              if (canvasBounds) {
                const nodeWidth = 320; // Approximate node width
                const nodeHeight = 280; // Approximate node height
                const padding = 50; // Reduced padding for less aggressive constraining

                // Get current viewport to check zoom level
                const viewport = getViewport();
                const isZoomedOut = viewport.zoom < 0.8;

                // Use dynamic padding based on zoom level
                const dynamicPadding = isZoomedOut ? 20 : padding;

                // Calculate constrained position
                let constrainedPosition = { ...change.position };

                // Only constrain if not too far zoomed out
                if (!isZoomedOut) {
                  // Constrain to left boundary
                  if (change.position.x < dynamicPadding) {
                    constrainedPosition.x = dynamicPadding;
                  }

                  // Constrain to right boundary
                  if (
                    change.position.x >
                    canvasBounds.width - nodeWidth - dynamicPadding
                  ) {
                    constrainedPosition.x =
                      canvasBounds.width - nodeWidth - dynamicPadding;
                  }

                  // Constrain to top boundary
                  if (change.position.y < dynamicPadding) {
                    constrainedPosition.y = dynamicPadding;
                  }

                  // Constrain to bottom boundary
                  if (
                    change.position.y >
                    canvasBounds.height - nodeHeight - dynamicPadding
                  ) {
                    constrainedPosition.y =
                      canvasBounds.height - nodeHeight - dynamicPadding;
                  }
                }

                const isNearLeftBoundary = change.position.x < dynamicPadding;
                const isNearRightBoundary =
                  change.position.x >
                  canvasBounds.width - nodeWidth - dynamicPadding;
                const isNearTopBoundary = change.position.y < dynamicPadding;
                const isNearBottomBoundary =
                  change.position.y >
                  canvasBounds.height - nodeHeight - dynamicPadding;

                if (
                  isNearLeftBoundary ||
                  isNearRightBoundary ||
                  isNearTopBoundary ||
                  isNearBottomBoundary
                ) {
                  console.warn(
                    "⚠️ NODE NEAR BOUNDARY - Constraining position!"
                  );
                  console.warn("Original position:", change.position);
                  console.warn("Constrained position:", constrainedPosition);
                  // eslint-disable-next-line no-use-before-define
                  console.warn("Canvas bounds:", canvasBounds);

                  // Use the constrained position instead
                  change.position = constrainedPosition;

                  // Auto-pan viewport if node is near boundary (only for small adjustments)
                  const viewport = getViewport();

                  if (canvasBounds) {
                    let newViewport = { ...viewport };
                    let shouldPan = false;
                    const panAmount = 20; // Reduced pan amount for subtle movement

                    // Only pan if we're not too far from the boundary (within 200px)
                    const maxPanDistance = 200;

                    // Pan down if node is near bottom and not too far out
                    if (
                      isNearBottomBoundary &&
                      change.position.y < maxPanDistance
                    ) {
                      newViewport.y -= panAmount;
                      shouldPan = true;
                    }

                    // Pan up if node is near top and not too far out
                    if (
                      isNearTopBoundary &&
                      change.position.y > -maxPanDistance
                    ) {
                      newViewport.y += panAmount;
                      shouldPan = true;
                    }

                    // Pan right if node is near left and not too far out
                    if (
                      isNearLeftBoundary &&
                      change.position.x < maxPanDistance
                    ) {
                      newViewport.x -= panAmount;
                      shouldPan = true;
                    }

                    // Pan left if node is near right and not too far out
                    if (
                      isNearRightBoundary &&
                      change.position.x > -maxPanDistance
                    ) {
                      newViewport.x += panAmount;
                      shouldPan = true;
                    }

                    if (shouldPan) {
                      setViewport(newViewport, { duration: 300 }); // Smooth transition
                    }
                  }
                }
              }

              // Update the node position using the existing updateNode method
              const node = viewModel.getNodeById(change.id);
              if (node) {
                viewModel.updateNode(change.id, {
                  ...node.data,
                  position: change.position,
                });
              } else {
                console.error("❌ Node not found in ViewModel:", change.id);
              }
            }
            break;
          case "dimensions":
            break;
          case "select":
            // Node selection changed
            break;
          default:
            console.log("Unhandled node change type:", change.type);
        }
      });
      console.log(
        "Current nodes after change:",
        nodes.map((n) => ({ id: n.id, position: n.position, type: n.type }))
      );
      console.log("=== END NODES CHANGE ===");
      onNodesChange(changes);
    },
    [
      viewModel,
      onNodesChange,
      nodes,
      reactFlowWrapper,
      getViewport,
      setViewport,
    ]
  );

  const handleEdgesChange = useCallback(
    (changes) => {
      if (readOnly) return;
      console.log("ReactFlow edges change:", changes);

      // Handle edge deletions to update ViewModel
      changes.forEach((change) => {
        if (change.type === "remove") {
          viewModel.deleteEdge(change.id);
        }
      });

      onEdgesChange(changes);
    },
    [readOnly, viewModel, onEdgesChange]
  );

  const handleDeleteEdge = useCallback(
    (edgeId) => {
      viewModel.deleteEdge(edgeId);
      onEdgesChange([{ type: "remove", id: edgeId }]);
    },
    [viewModel, onEdgesChange]
  );

  const edgesWithDeleteButton = React.useMemo(
    () =>
      readOnly
        ? edges.map((e) => ({
            ...e,
            type: "deletable",
            selectable: false,
            deletable: false,
            markerEnd: e.markerEnd ?? getFlowEdgeMarkerEnd(!!e.selected),
            style: { ...getFlowEdgeStyle(!!e.selected), ...(e.style || {}) },
          }))
        : edges.map((e) => ({
            ...e,
            type: "deletable",
            markerEnd: e.markerEnd ?? getFlowEdgeMarkerEnd(!!e.selected),
            style: { ...getFlowEdgeStyle(!!e.selected), ...(e.style || {}) },
            data: { ...(e.data || {}), onDelete: () => handleDeleteEdge(e.id) },
          })),
    [readOnly, edges, handleDeleteEdge]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      if (readOnly) return;
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
      const type = event.dataTransfer.getData("application/reactflow");

      if (typeof type === "undefined" || !type) {
        return;
      }

      if (reactFlowBounds) {
        // Use reliable positioning that works consistently
        const position = {
          x: event.clientX - reactFlowBounds.left,
          y: event.clientY - reactFlowBounds.top,
        };

        try {
          // Add child node management callbacks for interactive message nodes
          const nodeData =
            type === "interactiveMessage"
              ? {
                  onSectionsChange: handleSectionsChange,
                  onDeleteRowNode: handleDeleteRowNode,
                }
              : {};

          const newNode = viewModel.addNode(type, position, nodeData);
          console.log("Node created successfully:", newNode);

          // Clear the drag data
          event.dataTransfer.clearData();
        } catch (error) {
          console.error("Failed to add node:", error);
        }
      } else {
        console.error("React Flow not ready:", {
          reactFlowBounds: !!reactFlowBounds,
        });
      }
    },
    [readOnly, viewModel, reactFlowWrapper]
  );

  // Helper function to transform labels array for interactive_message nodes
  const transformLabelsForExport = (node) => {
    if (
      (node.type === "interactiveMessage" || node.type === "questionMessage") &&
      node.data &&
      node.data.labels &&
      Array.isArray(node.data.labels) &&
      node.data.labels.length > 0
    ) {
      // Check if labels are already in the new format (objects with label_id)
      const isNewFormat = node.data.labels.every(
        (label) => typeof label === "object" && label.label && label.label_id
      );

      if (!isNewFormat) {
        // Transform string array to object array
        const transformedLabels = node.data.labels.map((label, index) => ({
          label: label,
          label_id: `btn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${index}`,
        }));

        return {
          ...node,
          data: {
            ...node.data,
            labels: transformedLabels,
          },
        };
      }
    }
    return node;
  };

  // Helper function to transform sections for interactive_message nodes
  const transformSectionsForExport = (node) => {
    if (
      node.type === "interactiveMessage" &&
      node.data &&
      node.data.sections &&
      Array.isArray(node.data.sections)
    ) {
      // Check if sections are already in the new format (object with header and items)
      const isNewFormat =
        node.data.sections &&
        typeof node.data.sections === "object" &&
        !Array.isArray(node.data.sections) &&
        node.data.sections.header !== undefined &&
        node.data.sections.items !== undefined;

      if (!isNewFormat) {
        // Transform array to object with header and items
        const transformedSections = {
          header: node.data.listHeader || "",
          items: node.data.sections,
        };

        return {
          ...node,
          data: {
            ...node.data,
            sections: transformedSections,
          },
        };
      }
    }
    return node;
  };

  // Helper function to transform labels for import (reverse transformation)
  const transformLabelsForImport = (node) => {
    return node;
  };

  // Helper function to transform sections for import (reverse transformation)
  const transformSectionsForImport = (node) => {
    if (
      node.type === "interactiveMessage" &&
      node.data &&
      node.data.sections &&
      typeof node.data.sections === "object" &&
      !Array.isArray(node.data.sections) &&
      node.data.sections.header !== undefined &&
      node.data.sections.items !== undefined
    ) {
      // Transform object back to array and extract header
      const transformedSections = node.data.sections.items || [];
      const listHeader = node.data.sections.header || "";

      return {
        ...node,
        data: {
          ...node.data,
          sections: transformedSections,
          listHeader: listHeader,
        },
      };
    }
    return node;
  };

  // Import/Export functions
  const exportFlow = () => {
    if (readOnly) return;
    try {
      const nodes = viewModel.getNodes();

      const flowData = {
        nodes: nodes.map((node) => {
          let transformedNode = transformLabelsForExport(node);
          transformedNode = transformSectionsForExport(transformedNode);
          return {
            ...transformedNode,
            isUpdated:
              transformedNode.isUpdated !== undefined
                ? transformedNode.isUpdated
                : false,
          };
        }),
        edges: viewModel.getEdges(),
        viewport: getViewport(),
        metadata: {
          version: "1.0",
          exportedAt: new Date().toISOString(),
          nodeCount: nodes.length,
          edgeCount: viewModel.getEdges().length,
        },
      };

      const jsonString = JSON.stringify(flowData, null, 2);
      const blob = new Blob([jsonString], { type: "application/json" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `flow-export-${
        new Date().toISOString().split("T")[0]
      }.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: "Flow exported successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Export error:", error);
      setSnackbar({
        open: true,
        message: "Failed to export flow: " + error.message,
        severity: "error",
      });
    }
  };

  function getTemplateData() {
    try {
      const flowData = JSON.parse(templateData);
   
      // Validate the imported data
      if (!flowData.nodes || !Array.isArray(flowData.nodes)) {
        throw new Error("Invalid flow data: missing or invalid nodes");
      }

      if (!flowData.edges || !Array.isArray(flowData.edges)) {
        console.warn(
          "No edges found in import data, will use empty edges array"
        );
        flowData.edges = [];
      }

      // Clear current flow
      viewModel.clearFlow();

      // Import nodes with isUpdated attribute
      const importedNodes = [];
      flowData.nodes.forEach((node, index) => {
        try {
          // Apply import transformations to handle exported format
          let transformedNode = transformLabelsForImport(node);
          transformedNode = transformSectionsForImport(transformedNode);
          transformedNode = viewModel.service.migrateLegacyVisualSearchNode(transformedNode);
          console.log('transform', transformedNode);
          // Create node with original ID by directly adding to model
          const nodeToAdd = {
            id: transformedNode.id, // Preserve original ID
            type: transformedNode.type,
            position: transformedNode.position,
            data: {
              ...transformedNode.data,
              isUpdated:
                transformedNode.isUpdated !== undefined
                  ? transformedNode.isUpdated
                  : false,
            },
          };

          // Add node directly to the model to preserve ID
          viewModel.service.model.addNode(nodeToAdd);

          // Add proper handlers to the imported node
          const importedNode = viewModel.service.model.getNode(nodeToAdd.id);
          if (importedNode) {
            // Add delete handler
            importedNode.data.onDelete = (id) => {
              viewModel.deleteNode(id);
            };

            // Add type-specific handlers
            if (nodeToAdd.type === "normalMessage") {
              importedNode.data.onChangeText = (id, value) =>
                viewModel.updateNode(id, { text: value });
            } else if (nodeToAdd.type === "interactiveMessage") {
              importedNode.data.onChangeHeader = (id, value) =>
                viewModel.updateNode(id, { header: value });
              importedNode.data.onChangeBody = (id, value) =>
                viewModel.updateNode(id, { body: value });
              importedNode.data.onChangeFooter = (id, value) =>
                viewModel.updateNode(id, { footer: value });
              importedNode.data.onChangeLabels = (id, value) =>
                viewModel.updateNode(id, { labels: value });
              importedNode.data.onChangeSections = (id, value) =>
                viewModel.updateNode(id, { sections: value });
              importedNode.data.onChangeListHeader = (id, value) =>
                viewModel.updateNode(id, { listHeader: value });
              importedNode.data.onChangeMessageType = (id, value) =>
                viewModel.updateNode(id, { messageType: value });
            } else if (nodeToAdd.type === "mediaMessage") {
              importedNode.data.onChangeFile = (id, file) =>
                viewModel.updateNode(id, { selectedFile: file });
              importedNode.data.onChangeCaption = (id, caption) =>
                viewModel.updateNode(id, { caption });
              importedNode.data.onChangeMediaType = (id, mediaType) =>
                viewModel.updateNode(id, { mediaType });
              importedNode.data.onChangeFileName = (id, fileName) =>
                viewModel.updateNode(id, { fileName });
              importedNode.data.onChangeFilePath = (id, filePath) =>
                viewModel.updateNode(id, { filePath });
              importedNode.data.onChangePreviewUrl = (id, previewUrl) =>
                viewModel.updateNode(id, { previewUrl });
            } else if (nodeToAdd.type === 'catalogMessage') {
               importedNode.data.onChangeText = (id, value) =>
                viewModel.updateNode(id, { text: value });
               importedNode.data.onChangeProduct = (id, value) =>
              viewModel.updateNode(id, { products: value });
            } else if (nodeToAdd.type === "templateMessage") {
              importedNode.data.onChangeTemplate = (id, value) =>
                viewModel.updateNode(id, { templateId: value });
              importedNode.data.onChangeVariables = (id, value) =>
                viewModel.updateNode(id, { variables: value });
            } else if (nodeToAdd.type === "paymentMessage") {
              const paymentNodeId = nodeToAdd.id;
              importedNode.data.onChangePaymentType = (_id, value) =>
                viewModel.updateNode(paymentNodeId, { paymentType: value });
              importedNode.data.onChangeHeader = (_id, value) =>
                viewModel.updateNode(paymentNodeId, { header: value });
              importedNode.data.onChangeBody = (_id, value) =>
                viewModel.updateNode(paymentNodeId, { body: value });
              importedNode.data.onChangeFooter = (_id, value) =>
                viewModel.updateNode(paymentNodeId, { footer: value });
              importedNode.data.onChangePaymentItems = (_id, value) =>
                viewModel.updateNode(paymentNodeId, { paymentItems: value });
              importedNode.data.onChangeConfigurationId = (_id, value) =>
                viewModel.updateNode(paymentNodeId, { configurationId: value });
              importedNode.data.onChangeConfigurationName = (_id, value) =>
                viewModel.updateNode(paymentNodeId, { configurationName: value });
            }
          }

          importedNodes.push(nodeToAdd);
        } catch (error) {
          console.error(`Failed to import node ${node.id}:`, error);
          throw new Error(`Failed to import node ${node.id}: ${error.message}`);
        }
      });

      // Notify listeners after all nodes are imported
      viewModel.notifyListeners();

      // Verify nodes were imported with correct IDs
      const importedNodesFromModel = viewModel.getNodes();
      console.log("=== NODE IMPORT VERIFICATION ===", {
        expectedNodeIds: flowData.nodes.map((n) => n.id),
        actualNodeIds: importedNodesFromModel.map((n) => n.id),
        allMatch: flowData.nodes.every(
          (node, index) => node.id === importedNodesFromModel[index]?.id
        ),
      });

      // Import edges
      const importedEdges = [];
      flowData.edges.forEach((edge, index) => {
        try {
          // Check if source and target nodes exist
          const sourceNode = viewModel.getNodeById(edge.source);
          const targetNode = viewModel.getNodeById(edge.target);

          // Create edge with original ID
          const edgeToAdd = {
            id: edge.id, // Preserve original ID
            source: edge.source,
            target: edge.target,
            sourceHandle: edge.sourceHandle || null,
            targetHandle: edge.targetHandle || null,
            type: edge.type || "default",
            animated: edge.animated || false,
            style: edge.style || {},
          };

          // Add edge directly to the model to preserve ID
          viewModel.service.model.addEdge(edgeToAdd);

          // Verify edge was added to model
          const allEdges = viewModel.service.model.getAllEdges();
          const addedEdge = allEdges.find((e) => e.id === edge.id);

          importedEdges.push(edgeToAdd);
        } catch (error) {
          console.warn(`Failed to import edge ${edge.id}:`, error);
        }
      });

      // Notify listeners after all edges are imported
      viewModel.notifyListeners();

      // Final verification
      const finalNodes = viewModel.getNodes();
      const finalEdges = viewModel.getEdges();

      // Set viewport if available
      if (flowData.viewport) {
        setViewport(flowData.viewport);
      }

      setImportDialogOpen(false);
      setImportData("");

      if (!readOnly) {
        setSnackbar({
          open: true,
          message: `Flow imported successfully! ${flowData.nodes.length} nodes, ${flowData.edges.length} edges`,
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Import error:", error);
      setSnackbar({
        open: true,
        message: "Failed to import flow: " + error.message,
        severity: "error",
      });
    }
  }
  useEffect(() => {
    if (readOnly && previewTemplateJson != null) {
      const s = typeof previewTemplateJson === "string"
        ? previewTemplateJson
        : JSON.stringify(previewTemplateJson);
      setTemplateData(s);
      setTemplateId(null);
      return;
    }
    if (chatbotId != null && chatbotId !== "") {
      FlowService.getFlow(chatbotId).then((res) => {
        if (res) {
          setTemplateData(res.template_json);
          setTemplateId(res.id);
        } else {
          setTemplateData(null);
          setTemplateId(null);
        }
      });
    }
  }, [readOnly, previewTemplateJson, chatbotId]);

  useEffect(() => {
    if (templateData != null) {
      getTemplateData();
    }
  }, [templateData]);

  const importFlow = () => {
    try {

      const flowData = JSON.parse(importData);
      // Validate the imported data
      if (!flowData.nodes || !Array.isArray(flowData.nodes)) {
        throw new Error("Invalid flow data: missing or invalid nodes");
      }

      if (!flowData.edges || !Array.isArray(flowData.edges)) {
        console.warn(
          "No edges found in import data, will use empty edges array"
        );
        flowData.edges = [];
      }

      // Clear current flow
      viewModel.clearFlow();

      // Import nodes with isUpdated attribute
      const importedNodes = [];
      flowData.nodes.forEach((node, index) => {
        try {
          // Apply import transformations to handle exported format
          let transformedNode = transformLabelsForImport(node);
          transformedNode = transformSectionsForImport(transformedNode);
          transformedNode = viewModel.service.migrateLegacyVisualSearchNode(transformedNode);

          // Create node with original ID by directly adding to model
          const nodeToAdd = {
            id: transformedNode.id, // Preserve original ID
            type: transformedNode.type,
            position: transformedNode.position,
            data: {
              ...transformedNode.data,
              isUpdated:
                transformedNode.isUpdated !== undefined
                  ? transformedNode.isUpdated
                  : false,
            },
          };

          // Add node directly to the model to preserve ID
          viewModel.service.model.addNode(nodeToAdd);

          // Add proper handlers to the imported node
          const importedNode = viewModel.service.model.getNode(nodeToAdd.id);
          if (importedNode) {
            // Add delete handler
            importedNode.data.onDelete = (id) => {
              console.log("Direct delete called for imported node:", id);
              viewModel.deleteNode(id);
            };

            // Add type-specific handlers
            if (nodeToAdd.type === "normalMessage") {
              importedNode.data.onChangeText = (id, value) =>
                viewModel.updateNode(id, { text: value });
            } else if (nodeToAdd.type === "interactiveMessage") {
              importedNode.data.onChangeHeader = (id, value) =>
                viewModel.updateNode(id, { header: value });
              importedNode.data.onChangeBody = (id, value) =>
                viewModel.updateNode(id, { body: value });
              importedNode.data.onChangeFooter = (id, value) =>
                viewModel.updateNode(id, { footer: value });
              importedNode.data.onChangeLabels = (id, value) =>
                viewModel.updateNode(id, { labels: value });
              importedNode.data.onChangeSections = (id, value) =>
                viewModel.updateNode(id, { sections: value });
              importedNode.data.onChangeListHeader = (id, value) =>
                viewModel.updateNode(id, { listHeader: value });
              importedNode.data.onChangeMessageType = (id, value) =>
                viewModel.updateNode(id, { messageType: value });
            } else if (nodeToAdd.type === "mediaMessage") {
              importedNode.data.onChangeFile = (id, file) =>
                viewModel.updateNode(id, { selectedFile: file });
              importedNode.data.onChangeCaption = (id, caption) =>
                viewModel.updateNode(id, { caption });
              importedNode.data.onChangeMediaType = (id, mediaType) =>
                viewModel.updateNode(id, { mediaType });
              importedNode.data.onChangeFileName = (id, fileName) =>
                viewModel.updateNode(id, { fileName });
              importedNode.data.onChangeFilePath = (id, filePath) =>
                viewModel.updateNode(id, { filePath });
              importedNode.data.onChangePreviewUrl = (id, previewUrl) =>
                viewModel.updateNode(id, { previewUrl });
            } else if (nodeToAdd.type === "catalogMessage") {
              importedNode.data.onChangeText = (id, value) =>
                viewModel.updateNode(id, { text: value });
              importedNode.data.onChangeProduct = (id, value) =>
              viewModel.updateNode(id, { products: value });
            } else if (nodeToAdd.type === "templateMessage") {
              importedNode.data.onChangeTemplate = (id, value) =>
                viewModel.updateNode(id, { templateId: value });
              importedNode.data.onChangeVariables = (id, value) =>
                viewModel.updateNode(id, { variables: value });
            } else if (nodeToAdd.type === "paymentMessage") {
              const paymentNodeId = nodeToAdd.id;
              importedNode.data.onChangePaymentType = (_id, value) =>
                viewModel.updateNode(paymentNodeId, { paymentType: value });
              importedNode.data.onChangeHeader = (_id, value) =>
                viewModel.updateNode(paymentNodeId, { header: value });
              importedNode.data.onChangeBody = (_id, value) =>
                viewModel.updateNode(paymentNodeId, { body: value });
              importedNode.data.onChangeFooter = (_id, value) =>
                viewModel.updateNode(paymentNodeId, { footer: value });
              importedNode.data.onChangePaymentItems = (_id, value) =>
                viewModel.updateNode(paymentNodeId, { paymentItems: value });
              importedNode.data.onChangeConfigurationId = (_id, value) =>
                viewModel.updateNode(paymentNodeId, { configurationId: value });
              importedNode.data.onChangeConfigurationName = (_id, value) =>
                viewModel.updateNode(paymentNodeId, { configurationName: value });
            }
          }

          importedNodes.push(nodeToAdd);
        } catch (error) {
          console.error(`Failed to import node ${node.id}:`, error);
          throw new Error(`Failed to import node ${node.id}: ${error.message}`);
        }
      });

      // Notify listeners after all nodes are imported
      viewModel.notifyListeners();

      // Verify nodes were imported with correct IDs
      const importedNodesFromModel = viewModel.getNodes();

      // Import edges
      const importedEdges = [];
      flowData.edges.forEach((edge, index) => {
        try {

          // Check if source and target nodes exist
          const sourceNode = viewModel.getNodeById(edge.source);
          const targetNode = viewModel.getNodeById(edge.target);

          // Create edge with original ID
          const edgeToAdd = {
            id: edge.id, // Preserve original ID
            source: edge.source,
            target: edge.target,
            sourceHandle: edge.sourceHandle || null,
            targetHandle: edge.targetHandle || null,
            type: edge.type || "default",
            animated: edge.animated || false,
            style: edge.style || {},
          };

          // Add edge directly to the model to preserve ID
          viewModel.service.model.addEdge(edgeToAdd);

          // Verify edge was added to model
          const allEdges = viewModel.service.model.getAllEdges();
          const addedEdge = allEdges.find((e) => e.id === edge.id);

          importedEdges.push(edgeToAdd);
        } catch (error) {
          console.warn(`Failed to import edge ${edge.id}:`, error);
        }
      });

      // Notify listeners after all edges are imported
      viewModel.notifyListeners();

      // Final verification
      const finalNodes = viewModel.getNodes();
      const finalEdges = viewModel.getEdges();

      // Set viewport if available
      if (flowData.viewport) {
        setViewport(flowData.viewport);
      }

      setImportDialogOpen(false);
      setImportData("");

      setSnackbar({
        open: true,
        message: `Flow imported successfully! ${flowData.nodes.length} nodes, ${flowData.edges.length} edges`,
        severity: "success",
      });
    } catch (error) {
      console.error("Import error:", error);
      setSnackbar({
        open: true,
        message: "Failed to import flow: " + error.message,
        severity: "error",
      });
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImportData(e.target.result);
      };
      reader.readAsText(file);
    }
  };

  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData("application/reactflow", nodeType);
    event.dataTransfer.effectAllowed = "move";
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {readOnly && (
        <div
          style={{
            padding: "10px 16px",
            borderBottom: "1px solid #e0e0e0",
            display: "flex",
            alignItems: "center",
            gap: 12,
            background: "#fafafa",
            flexShrink: 0,
            flexWrap: "wrap",
          }}
        >
          <Button variant="outlined" size="small" onClick={() => onBack && onBack()}>
            Back
          </Button>
          <span style={{ fontSize: 14, color: "#555" }}>
            View only. Add this chatbot to your account to edit the flow.
          </span>
          {defaultChatbotIdForClone && (
            <Button variant="contained" size="small" onClick={handleCloneDefaultChatbot}>
              Use this chatbot
            </Button>
          )}
        </div>
      )}
    <div style={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>
      {/* Sidebar */}
      {readOnly ? (
      <div
        style={{
          width: 260,
          background: "#f8f9fa",
          borderRight: "1px solid #dee2e6",
          padding: "20px",
          overflowY: "auto",
          flexShrink: 0,
        }}
      >
        <h3 style={{ marginBottom: "8px", color: "#333" }}>Preview</h3>
        <p style={{ color: "#666", fontSize: 12, lineHeight: 1.5 }}>
          This is a read-only view. Drag the canvas to pan. To change anything, use &quot;Use this chatbot&quot; — then edit under Chatbots.
        </p>
      </div>
      ) : (
      <div
        style={{
          width: 248,
          background: "#f8fafc",
          borderRight: "1px solid #e2e8f0",
          padding: "14px 12px",
          overflowY: "auto",
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", mb: 0.5 }}>
          Components
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "#64748b", display: "block", mb: 1.75, lineHeight: 1.45 }}
        >
          Drag a block onto the canvas to build your flow.
        </Typography>

        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 6,
            }}
          >
            {FLOW_PALETTE_ITEMS.map((item) => (
              <FlowPaletteDraggable
                key={item.type}
                dragType={item.type}
                label={item.label}
                Icon={item.Icon}
                accent={item.accent}
                onDragStart={onDragStart}
                disabled={item.catalogOnly && !hasCatalogConnected}
                title={
                  item.catalogOnly && !hasCatalogConnected
                    ? "Connect a WhatsApp catalog for this service first"
                    : ""
                }
              />
            ))}
          </div>

          {/* Import/Export Section */}
          <div
            style={{
              marginTop: "30px",
              borderTop: "1px solid #dee2e6",
              paddingTop: "20px",
            }}
          >
            <h4
              style={{ marginBottom: "15px", color: "#333", fontSize: "14px" }}
            >
              Flow Management
            </h4>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {/* Save Button */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<Save />}
                onClick={handleSaveFlow}
                style={{
                  borderColor: "#ffc107",
                  color: "#ffc107",
                  fontSize: "12px",
                  padding: "8px 12px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#ffc107";
                  e.currentTarget.style.color = "white";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "#ffc107";
                }}
              >
                Save Chatbot
              </Button>

              {/* Export Button */}
              {/* <Button
                variant="outlined"
                size="small"
                startIcon={<Download />}
                onClick={exportFlow}
                style={{
                  borderColor: "#28a745",
                  color: "#28a745",
                  fontSize: "12px",
                  padding: "8px 12px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#28a745";
                  e.currentTarget.style.color = "white";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "#28a745";
                }}
              >
                Export Flow
              </Button> */}

              {/* Import Button */}
              {/* <Button
                variant="outlined"
                size="small"
                startIcon={<Upload />}
                onClick={() => setImportDialogOpen(true)}
                style={{
                  borderColor: "#007bff",
                  color: "#007bff",
                  fontSize: "12px",
                  padding: "8px 12px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#007bff";
                  e.currentTarget.style.color = "white";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "#007bff";
                }}
              >
                Import Flow
              </Button> */}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Import Dialog */}
      {!readOnly && (
      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import Flow</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload a JSON file or paste JSON data to import a flow:
            </Typography>

            {/* File Upload */}
            <Button
              variant="outlined"
              component="label"
              startIcon={<FolderOpen />}
              sx={{ mb: 2, mr: 2 }}
            >
              Choose File
              <input
                type="file"
                hidden
                accept=".json"
                onChange={handleFileUpload}
              />
            </Button>

            {/* JSON Text Area */}
            <textarea
              value={importData}
              onChange={(e) => setImportData(e.target.value)}
              placeholder="Paste JSON data here..."
              style={{
                width: "100%",
                minHeight: "200px",
                padding: "12px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontFamily: "monospace",
                fontSize: "12px",
                resize: "vertical",
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
          <Button onClick={importFlow} variant="contained" color="primary">
            Import Flow
          </Button>
        </DialogActions>
      </Dialog>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* React Flow Canvas */}
      <div
        style={{
          flexGrow: 1,
          position: "relative",
          overflow: "visible", // Changed from "hidden" to "visible"
          zIndex: 1,
        }}
        ref={reactFlowWrapper}
      >
        <ReactFlow
          nodes={nodes}
          edges={edgesWithDeleteButton}
          onNodesChange={handleNodesChange}
          onEdgesChange={handleEdgesChange}
          onConnect={onConnect}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={
            readOnly
              ? {
                type: "default",
                selectable: false,
                deletable: false,
                markerEnd: getFlowEdgeMarkerEnd(false),
                style: getFlowEdgeStyle(false),
              }
              : FLOW_DEFAULT_EDGE_OPTIONS
          }
          connectionLineComponent={FlowConnectionLine}
          connectionLineStyle={getFlowEdgeStyle(true)}
          nodesDraggable={!readOnly}
          nodesConnectable={!readOnly}
          elementsSelectable={!readOnly}
          deleteKeyCode={readOnly ? null : ["Backspace", "Delete"]}
          edgesReconnectable={!readOnly}
          onReconnect={onReconnect}
          onDragOver={onDragOver}
          onDrop={onDrop}
          panOnDrag
          onMove={(event, viewport) => {
            console.log(
              "Canvas wrapper:",
              reactFlowWrapper.current?.getBoundingClientRect()
            );
          }}
          onViewportChange={(viewport) => {
            console.log("=== REACTFLOW VIEWPORT CHANGE ===");
            console.log("New viewport:", viewport);
            console.log(
              "Current nodes:",
              nodes.map((n) => ({ id: n.id, position: n.position }))
            );
            console.log("=== END VIEWPORT CHANGE ===");
          }}
          onNodeDragStart={(event, node) => {
            console.log("=== NODE DRAG START ===");
            console.log("Node:", {
              id: node.id,
              position: node.position,
              type: node.type,
            });
            console.log("Event:", event);
            console.log(
              "Canvas bounds:",
              reactFlowWrapper.current?.getBoundingClientRect()
            );
            console.log("=== END NODE DRAG START ===");
          }}
          onNodeDrag={(event, node) => {
            console.log("=== NODE DRAGGING ===");
            console.log("Node:", {
              id: node.id,
              position: node.position,
              type: node.type,
            });
            console.log("Event:", event);
            console.log(
              "Canvas bounds:",
              reactFlowWrapper.current?.getBoundingClientRect()
            );
            console.log("=== END NODE DRAGGING ===");
          }}
          onNodeDragStop={(event, node) => {
            console.log("=== NODE DRAG STOP ===");
            console.log("Node:", {
              id: node.id,
              position: node.position,
              type: node.type,
            });
            console.log("Event:", event);
            console.log(
              "Canvas bounds:",
              reactFlowWrapper.current?.getBoundingClientRect()
            );
            console.log("=== END NODE DRAG STOP ===");
          }}
          nodeTypes={nodeTypes}
          fitView={false} // Disabled fitView to prevent automatic viewport adjustments
          minZoom={0.1}
          maxZoom={2}
          defaultViewport={{ x: 0, y: 0, zoom: 1 }}
          style={{ width: "100%", height: "100%", zIndex: 1 }}
        >
          <Background />
          <Controls />
          <MiniMap />
        </ReactFlow>
      </div>
    </div>
    </div>
  );
};

export const FlowCanvas = ({
  automationId,
  readOnly = false,
  previewTemplateJson = null,
  defaultChatbotIdForClone = null,
  onBack = null,
  onCloned = null,
}) => {
  const viewModel = useFlowViewModel();
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        margin: 0,
        padding: 0,
        overflow: "hidden",
      }}
    >
      <ReactFlowProvider>
        <FlowCanvasInner
          viewModel={viewModel}
          reactFlowWrapper={reactFlowWrapper}
          chatbotId={readOnly ? null : automationId}
          readOnly={readOnly}
          previewTemplateJson={previewTemplateJson}
          defaultChatbotIdForClone={defaultChatbotIdForClone}
          onBack={onBack}
          onCloned={onCloned}
          nodes={nodes}
          setNodes={setNodes}
          edges={edges}
          setEdges={setEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
        />
      </ReactFlowProvider>
    </div>
  );
};
